import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect, useRef, useState } from "react";
import useWorkspaces from '../../../hooks/useWorkspaces';
import { scrollbarStyle } from "../../../shared/dashboard";
import { User } from "../../../types/common";
import { Workspace } from "../../../types/files";
import InvestorsIconFC from '../../atoms/InvestorsIconFC';
import WorkspacesLookUpInvite from "../../molecules/workspaces-search-create/WorkspacesLookUpInvite";
import { FallbackLoading } from "../../templates/loader";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: '600px',
            height: 'fit-content',
            padding: '16px 24px',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.3rem',
        gap: '0.5rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        height: '100%',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 0,
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: 8,
            padding: '0 8px !important',
            background: '#fff',
            "& > input, > textarea": {
                fontFamily: 'Lato',
                fontSize: '1.2rem',
                color: '#048290',
                padding: 'unset !important',
            },
            "& fieldset": {
              	borderColor: 'darkgray !important',
            },
            "&:hover fieldset": {
              	borderColor: 'black !important',
            },
            "&.Mui-focused fieldset": {
              	borderColor: `${theme.palette.primary.main} !important`,
            },
        },
    },
    checkBox: {
        padding: 'unset !important',
        marginRight: '4px !important',
        "& > svg": {
            width: '20px !important',
            height: '20px !important',
        }
    },
    removeButton: {
        width: 'auto',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: theme.palette.warning.dark,
        textTransform: 'none !important' as any,
        transition: 'ease-in-out 300ms',
        "&:hover": {
            background: 'transparent',
        }
    },
    commonButton: {
        minWidth: 96,
        borderRadius: 32,
        textTransform: 'none',
        fontWeight: 'bold',
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sublabel: {
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    label1: {
        fontSize: '1rem',
        fontWeight: 500,
        color: theme.colors.orange['600'],
    },
    label2: {
        fontSize: '1rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
    },
    iconButton: {
        minWidth: 32,
        padding: 'unset !important',
        fontFamily: 'Inter !important',
        fontSize: '0.95rem',
        fontWeight: 400,
        color: theme.palette.primary.main,
        textTransform: 'none',
        "& > .MuiButton-startIcon": {
            width: 'auto',
            height: 32,
            padding: 4,
            "& > .MuiSvgIcon-root": {
                fontSize: 24,
                color: theme.palette.primary.main,
            }
        },
        "&:hover": {
            backgroundColor: 'inherit',
        },
    },
    investors :{
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: 'black',
    },
}));

const InvestorsModal: React.FC<{
    isOpen: boolean,
    sharedWorkspace: Workspace,
    onClose: () => void,
}> = ({ isOpen, sharedWorkspace, onClose }) => {
    const classes = useStyles();
    const { acceptedInvestors, fetchWorkspaceUserMemberships } = useWorkspaces();

    const [loading, setLoading] = useState<boolean>(true);
    const [investors, setInvestors] = useState<User[]>([]);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        fetchWorkspaceUserMemberships(sharedWorkspace).then(() => setLoading(false));
    // eslint-disable-next-line
    }, [sharedWorkspace]);

    useEffect(() => {
        timerRef.current = setTimeout(() => {
            clearTimeout(timerRef.current);
            setInvestors((acceptedInvestors
                .filter(investor => investor.workspaceId === sharedWorkspace.id)
                .map(user => ({ email: user.email, username: user.name })) ?? []) as User[]);
        }, 500);

        return () => {
            clearTimeout(timerRef.current);
        }
    // eslint-disable-next-line
    }, [acceptedInvestors]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                {`Investors in ${sharedWorkspace?.name}`}
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogContent className={classes.dialogContent}>
                <WorkspacesLookUpInvite invitedUsers={investors} mediumFit />
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" mt={2}>
                    <Box width={24} />
                    <Typography fontFamily="Inter" fontSize="1rem" fontWeight="bold" color="black">{'Members'}</Typography>
                </Stack>
                <Divider sx={{ margin: '8px 0', width: '100%' }} />
                {loading ? (<FallbackLoading height="300px" />) : (<>
                    {!!investors.length ? (
                        <Stack width="100%" minHeight={300} maxHeight={500} height="fit-content" overflow="auto" sx={{ ...scrollbarStyle }}>
                            {investors.map(user => (
                                <Stack spacing={1} width="100%" mt={1}>
                                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start" width="100%">
                                        <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                                        <Typography
                                            fontFamily="Inter"
                                            fontSize="1rem"
                                            fontWeight="bold"
                                            color="#666666"
                                            textAlign="center">
                                            {user.email}
                                        </Typography>
                                    </Stack>
                                    <Divider sx={{ margin: '8px 0', width: '100%' }} />
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Stack spacing={1} alignItems="center" justifyContent="center" height={300}>
                            <InvestorsIconFC width={60} height={60} color="lightgray" />
                            <Typography fontFamily="Inter" fontSize="1.3rem" fontWeight="bold" color="black" textAlign="center">
                                {'No investors found'}<br/>
                                {'in this workspace.'}
                            </Typography>
                        </Stack>
                    )}
                </>)}
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Typography className={classes.investors}>
                    {`${investors.length} investors`}
                </Typography>
                <Button variant="contained"
                    className={classes.commonButton}
                    onClick={() => onClose()}
                > {'Close'} </Button>
            </DialogActions>
        </Dialog>
    </>);
}

export default InvestorsModal;
